import { useRef, useEffect } from 'react';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/en.json';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './EntityListView.css';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useNavigate } from 'react-router-dom';

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const roleBadgeClass = (role) => {
  switch (String(role).toUpperCase()) {
    case 'ADMIN':
    case 'BUSINESS_ADMIN':
      return 'badge rounded-pill text-bg-primary'; 
    case 'AGENT':
      return 'badge rounded-pill text-bg-success'; 
    default:
      return 'badge rounded-pill text-bg-secondary';
  }
};

const formatRole = (role) => {
  const s = String(role).toLowerCase().replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const EntityListView = ({
  data,
  getCountry,
  onEdit,
  onDelete,
  emptyMessage = 'No items found.',
  isLoading = false,
  base,
  renderItem,
  headerSlot,
}) => {
  
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wasFocused = useRef(false);
  const isAdmin = useIsAdmin();

  useEffect(() => {
    if (wasFocused.current) {
      inputRef.current?.focus({ preventScroll: true });
      const el = inputRef.current;
      const len = el?.value?.length ?? 0;
      el?.setSelectionRange?.(len, len);
    }
  }, [data]);

  const navigateTo = (uuid) => {
    navigate(`/home/${base}/${uuid}`);
  };

  return (
    <div className="entity-container w-100">
      {isLoading && (
        <div className="d-flex flex-column gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card border-0 rounded-4">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <span className="placeholder-glow">
                      <span className="placeholder rounded" style={{ width: 24, height: 18 }} />
                    </span>
                    <span className="placeholder-glow">
                      <span className="placeholder col-6" />
                    </span>
                  </div>
                  <div className="d-flex gap-2">
                    <span className="btn btn-sm btn-outline-secondary disabled placeholder col-2" />
                    <span className="btn btn-sm btn-outline-secondary disabled placeholder col-2" />
                  </div>
                </div>
                <div className="mt-2 placeholder-glow">
                  <span className="placeholder col-4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && headerSlot}

      {!isLoading && (
        <div className="d-flex flex-column gap-3 mb-3">
          {data.length > 0 ? (
            data.map((item) => {
              const code = getCountry(item)?.toLowerCase();
              const flagSrc = code ? `https://flagcdn.com/24x18/${code}.png` : null;
              const extra =
                typeof renderItem === 'function'
                  ? renderItem({ item, flagSrc, isAdmin, navigateTo })
                  : null;

              return (
                <div
                  key={item.id}
                  className="card rounded-4 item-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigateTo(item.uuid)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigateTo(item.uuid);
                    }
                  }}
                >
                  <div className="card-body">
                    {extra && (
                      <div className="mb-3">
                        <div className="bg-body-tertiary border rounded-3 px-3 py-2 d-flex align-items-start justify-content-between">
                          <div className="d-flex align-items-center gap-2">{extra}</div>

                          {Array.isArray(item?.roles) && item.roles.length > 0 && (
                            <div
                              className="d-flex flex-column align-items-end ms-3"
                            >
                              <div className="d-flex flex-wrap gap-2 justify-content-end mt-1">
                                {item.roles.map((r, idx) => (
                                  <span
                                    key={`${String(r)}-${idx}`}
                                    className={roleBadgeClass(r)}
                                    title={String(r)}
                                  >
                                    {formatRole(r)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center">
                        <div className="me-2 d-flex align-items-center">
                          {flagSrc ? (
                            <img
                              src={flagSrc}
                              alt={getCountry(item) || 'Country'}
                              className="rounded shadow-sm"
                              width="24"
                              height="18"
                              loading="lazy"
                              style={{ objectFit: 'cover' }}
                            />
                          ) : (
                            <span className="text-body-secondary small">🏳️</span>
                          )}
                        </div>

                        <div className="d-flex flex-column">
                          <h6 className="mb-0 fw-bold">
                            {item.firstName} {item.lastName}
                          </h6>
                          <small className="text-body-secondary">
                            <strong>Phone:</strong> {item.phone || '—'}
                          </small>
                        </div>
                      </div>

                      {isAdmin && (
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary rounded-circle"
                            title="Edit"
                            aria-label="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit?.(item.id);
                            }}
                          >
                            <i className="bi bi-pencil" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger rounded-circle"
                            title="Delete"
                            aria-label="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete?.(item.id);
                            }}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body text-center text-body-secondary">{emptyMessage}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EntityListView;
